import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SrpTopRolesStrip } from './SrpTopRolesStrip';
import { useTopRolesStripData } from '../hooks/useTopRolesStripData';
import type { SrpTopRolesStripItem } from '../types/srp-top-roles-strip';

export interface TopRolesStripWithClientFetchProps {
  /** From slim/full page data when present; otherwise fetched client-side. */
  initialRoles?: SrpTopRolesStripItem[] | null;
  locale: string;
  /** When true, do not fetch or show the strip (location header / single-location SRP). */
  hideStrip?: boolean;
}

/**
 * Top-roles teaser on the SearchResultsPage / static SRP paths: fills from SSR when available,
 * otherwise loads `/api/top-roles-strip` after navigation.
 */
export function TopRolesStripWithClientFetch({
  initialRoles,
  locale,
  hideStrip = false,
}: TopRolesStripWithClientFetchProps): React.ReactElement | null {
  const intl = useIntl();
  const { roles, isPending } = useTopRolesStripData(initialRoles, {
    allowClientFetch: !hideStrip,
  });

  const labels = useMemo(
    () => ({
      label: intl.formatMessage({
        id: 'search.topRolesStrip.label',
        defaultMessage: 'In-demand roles',
        description: 'Heading for the compact top-roles row on the search results page',
      }),
      hubCta: intl.formatMessage({
        id: 'search.topRolesStrip.hubCta',
        defaultMessage: 'Salary data for Finland roles →',
        description: 'Link from SRP top-roles teaser to the salary hub',
      }),
    }),
    [intl]
  );

  return <SrpTopRolesStrip roles={roles} locale={locale} labels={labels} isPending={isPending} />;
}
